import React, { useEffect, useMemo, useState } from 'react';
import Fade from 'embla-carousel-fade';
import { EmblaCarouselType, EmblaPluginType } from 'embla-carousel';
import { ImageCropParamKeys, ImageGalleryRawImage, ImageParams } from '../ImageGallery.types';
import { Carousel, CarouselViewport, CarouselSlide } from '../../EmblaCarousel';
import { Picture } from '../../Picture';
import { useBreakpoints } from '../../../hooks';
import { CAROUSEL_CROP_DIMS } from '../ImageGallery.constants';
import { Styler } from '../../Styler';
import { CrossIcon } from '../../../icons';
import { ImageBase } from '../../ImageBase';
import { CarouselDotButtons } from '../../EmblaCarousel/CarouselDotButtons/CarouselDotButtons';
import { CarouselButton } from '../../EmblaCarousel/CarouselButton/CarouselButton';
import { buildResponsiveImageSet } from '../utils/buildResponsiveImageSet';
import { FixMeLater } from '../../../utils';

export interface ImageGalleryCarouselProps {
    /**
     * An array of enriched image information.
     */
    images: Array<ImageGalleryRawImage>;

    /**
     * The width and height parameters used to request image crops.
     * @default Object: { widthKey: "w", heightKey: "h" }
     */
    cropParamKeys?: ImageCropParamKeys;

    /**
     * Any additonal parameters which should be appended when requesting image crops.
     */
    additionalCropParams?: ImageParams;

    /**
     * The index of the slide which will be initially displayed.
     * @default 0
     */
    initialSlide?: number;

    /**
     * Callback fired when the gallery requests to be closed.
     */
    onClose?: () => void;

    /**
     * A title for the carousel. Used for accessibility markup.
     */
    title: string;

    /**
     * A reference passed to the root element.
     */
    ref?: React.Ref<FixMeLater>;
}

export function ImageGalleryCarousel(props: Readonly<ImageGalleryCarouselProps>): React.JSX.Element {
    const {
        images,
        cropParamKeys = { widthKey: 'w', heightKey: 'h' },
        additionalCropParams = {},
        initialSlide,
        onClose,
        title,
        ref,
        ...other
    } = props;

    const [emblaApi, setEmblaApi] = useState<EmblaCarouselType | null>(null);

    // Track the current slide incase we have to re-init due to breakpoint changes.
    const [currentSlide, setCurrentSlide] = useState(initialSlide ?? 0);
    const currentSlideRef = React.useRef(currentSlide);

    useEffect(() => {
        currentSlideRef.current = currentSlide;
    }, [currentSlide]);

    // We want to fade between slides at larger breakpoints.
    const { smallUp } = useBreakpoints();
    const fadeEnabled = smallUp;

    // Memoize Embla plugins based on breakpoint requirements.
    const plugins = useMemo<EmblaPluginType[]>(() => {
        return fadeEnabled ? [Fade()] : [];
    }, [fadeEnabled]);

    // Embla options
    const options = useMemo(() => ({ loop: false }), []);

    // Re-init Embla if plugins or options change, ensuring that we re-display the current slide.
    useEffect(() => {
        if (emblaApi) {
            emblaApi.reInit({ ...options, startIndex: currentSlideRef.current }, plugins);
        }
    }, [emblaApi, options, plugins]);

    return (
        <Carousel
            emblaOptions={options}
            emblaPlugins={plugins}
            getEmblaApi={setEmblaApi}
            slideSpacing={4}
            visibleSlides={1}
            initialSlide={initialSlide}
            onSlideChange={setCurrentSlide}
            title={title}
        >
            <Styler ref={ref} display="flex" flexDirection="column" w="100%" h="100%" bg="common.black" {...other}>
                <CarouselViewport h="100%" ArrowButtonProps={{ withBorder: false, size: 'l' }}>
                    {images.map((image) => {
                        const imageSet = buildResponsiveImageSet(
                            image.url,
                            CAROUSEL_CROP_DIMS,
                            cropParamKeys,
                            additionalCropParams
                        );

                        return (
                            <CarouselSlide key={image.id}>
                                <ImageBase
                                    src={imageSet.xs}
                                    pos="absolute"
                                    w="100%"
                                    h="100%"
                                    objectFit="cover"
                                    opacity="0.4 !important"
                                    transform="scale(1.2)"
                                    filter="blur(24px)"
                                    disableLazy={true}
                                />

                                <Picture imageSet={imageSet} pos="absolute" w="100%" h="100%" objectFit="contain" />
                            </CarouselSlide>
                        );
                    })}
                </CarouselViewport>

                <Styler pos="absolute" bottom={4} left={0} right={0} m="0 auto" w="fit-content">
                    <CarouselDotButtons />
                </Styler>

                {/* Close button */}
                <CarouselButton
                    icon={<CrossIcon biDirectional={true} />}
                    pos="absolute"
                    top={4}
                    right={4}
                    onClick={onClose}
                    size="l"
                />
            </Styler>
        </Carousel>
    );
}
